import { useCallback } from 'react';
import {
  AttendanceInquiryStatus,
  BulkRespondAttendanceInquiriesRequest,
  BulkRespondAttendanceInquiriesResponse,
  GetAttendanceInquiriesResponse,
} from '@classpass/protobuf-definitions/lib/web/models/partners/attendance_inquiry';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import URLS from 'constants/urls';
import { transformKeysToCamelCase, transformKeysToSnakeCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { selectActiveVenueId } from 'selectors/general';

export const attendanceInquiryKeys = {
  all: ['attendanceInquiries'],
  byVenue: venueId => [...attendanceInquiryKeys.all, venueId],
  detail: (venueId, inquiryId) => [...attendanceInquiryKeys.byVenue(venueId), inquiryId],
};

async function fetchInquiries(venueId, inquiryId) {
  try {
    // Always fetch only OPEN status inquiries
    // Add error handling for missing AttendanceInquiryStatus
    if (!AttendanceInquiryStatus?.ATTENDANCE_INQUIRY_STATUS_OPEN) {
      const error = new Error('AttendanceInquiryStatus is not available or missing ATTENDANCE_INQUIRY_STATUS_OPEN');
      reportError(error);
      return [];
    }

    const status = AttendanceInquiryStatus[AttendanceInquiryStatus.ATTENDANCE_INQUIRY_STATUS_OPEN];
    const url = URLS.home.attendanceInquiries(venueId, inquiryId, status);
    const response = await httpClient.get(url);
    const parsedResponse = GetAttendanceInquiriesResponse.fromJSON(response?.data || {});
    return transformKeysToCamelCase(parsedResponse?.inquiries || []);
  } catch (error) {
    reportError(error);
    return [];
  }
}

export function useAttendanceInquiriesQuery(inquiryId = null, options = {}) {
  const venueId = useSelector(selectActiveVenueId);

  return useQuery({
    queryKey: attendanceInquiryKeys.detail(venueId, inquiryId || 'all'),
    queryFn: () => fetchInquiries(venueId, inquiryId),
    enabled: !!venueId,
    ...options,
  });
}

export function useRespondToInquiriesBulkMutation(options = {}) {
  const queryClient = useQueryClient();
  const venueId = useSelector(selectActiveVenueId);

  return useMutation({
    mutationFn: async responses => {
      try {
        if (!responses || !Array.isArray(responses) || responses.length === 0) {
          throw new Error('Invalid responses array provided to respondToAttendanceInquiriesBulk');
        }

        const payload = BulkRespondAttendanceInquiriesRequest.toJSON(
          transformKeysToSnakeCase({
            inquiries: responses.map(r => ({
              inquiryId: r.inquiryId,
              status: r.status,
            })),
          })
        );
        const url = URLS.home.respondAttendanceInquiriesBulk();
        const response = await httpClient.post(url, payload);
        return transformKeysToCamelCase(BulkRespondAttendanceInquiriesResponse.fromJSON(response?.data || {}));
      } catch (error) {
        reportError(error);
        throw error;
      }
    },
    onSuccess: () => {
      if (options.invalidateOnSuccess !== false) {
        queryClient.invalidateQueries({ queryKey: attendanceInquiryKeys.byVenue(venueId) });
      }
    },
    ...options,
  });
}

export function useFetchAttendanceInquiries() {
  const queryClient = useQueryClient();
  const venueId = useSelector(selectActiveVenueId);

  return useCallback(
    async inquiryId => {
      if (!venueId) {
        return [];
      }

      try {
        const result = await queryClient.fetchQuery({
          queryKey: attendanceInquiryKeys.detail(venueId, inquiryId || 'all'),
          queryFn: () => fetchInquiries(venueId, inquiryId),
        });
        return result;
      } catch (error) {
        reportError(error);
        return [];
      }
    },
    [queryClient, venueId]
  );
}

export function useInvalidateAttendanceInquiries() {
  const queryClient = useQueryClient();
  const venueId = useSelector(selectActiveVenueId);

  return useCallback(() => {
    if (venueId) {
      queryClient.invalidateQueries({ queryKey: attendanceInquiryKeys.byVenue(venueId) });
    }
  }, [queryClient, venueId]);
}
